import Link from 'next/link';
import { memo } from 'react';

import {
  formatMonthlyPrice,
  type PlanInfoType,
  type UpgradePlanModalBillingCycle,
} from '~/components/Modals/PlansModal/utils/planInfo';
import { Routes } from '~/constants/routes';

export type UpgradePlanModalSidebarProps = {
  currentPlan: PlanInfoType | null;
  billingCycle: UpgradePlanModalBillingCycle;
};

export const UpgradePlanModalSidebar = memo(({ currentPlan, billingCycle }: UpgradePlanModalSidebarProps) => {
  const title =
    currentPlan && (currentPlan.planId === 'basic' || currentPlan.isLegacy)
      ? 'Find your new plan'
      : 'Upgrade your plan';

  return (
    <div
      data-testid="UPGRADE_PLAN_MODAL_SIDEBAR"
      className="flex flex-shrink-0 flex-col gap-8 ads:bg-base bg-white px-6 py-6 shadow-kanban sm:rounded-t-8 lg:w-[280px] lg:rounded-l-8 lg:rounded-tr-none lg:py-18 dark:bg-gray-1"
    >
      <p className="font-semibold ads:text-primary text-24 text-gray-12">{title}</p>
      {currentPlan && (
        <div className="flex flex-col gap-2">
          <p className="font-semibold ads:text-metadata text-12 text-gray-10 uppercase tracking-wide">Current plan</p>
          <div className="rounded-12 border ads:border-subtle border-gray-2 ads:bg-subtle bg-gray-3 p-4">
            <p className="font-control ads:text-primary text-32 text-gray-12">{currentPlan.planTitle}</p>
            {currentPlan.isLegacy && <p className="-mt-1 ads:text-metadata text-14 text-gray-10">(Legacy)</p>}
            <div className="mt-2 mb-4 flex items-end gap-1.5">
              <span className="font-semibold ads:text-metadata text-24 text-gray-10 leading-none">
                {formatMonthlyPrice({ billingCycle, prices: currentPlan.prices })}
              </span>{' '}
              <div className="flex flex-col font-semibold ads:text-metadata text-10 text-gray-9">
                <span>{currentPlan.pricingModel === 'perMember' ? 'per member per' : 'per'} month</span>
                <span>billed {billingCycle === 'annual' ? 'annually' : 'monthly'}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 text-14">
              {currentPlan.credits && !currentPlan.isLegacy && (
                <p className="font-semibold ads:text-primary text-gray-12">{currentPlan.credits}</p>
              )}
              {currentPlan.options?.map((option) => (
                <p className="ads:text-metadata text-gray-10" key={option}>
                  {option}
                </p>
              ))}
            </div>
          </div>
          {currentPlan.isLegacy && (
            <p className="ads:text-primary text-12 text-gray-12">
              This plan is no longer available. Please contact <Link href="mailto:help@air.inc">help@air.inc</Link> to
              explore your options.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <p className="font-semibold ads:text-metadata text-12 text-gray-10 uppercase tracking-wide">How credits work</p>
        <p className="ads:text-primary text-12 text-gray-12">
          Credits are a usage balance in your workspace that power Canvas generations, Workspace Agent tasks, and MCP
          tool calls. They refresh each month and can be topped up or upgraded as needed.{' '}
          <Link className="font-medium" href={Routes.creditsHelp} rel="noreferrer noopener" target="_blank">
            Learn more
          </Link>
        </p>
      </div>
    </div>
  );
});

UpgradePlanModalSidebar.displayName = 'UpgradePlanModalSidebar';
